import { useState } from 'react';
import { X, Play, Loader2, AlertCircle } from 'lucide-react';
import { useLocation } from 'wouter';
import { useAuth } from '@clerk/clerk-react';
import { useCoachingSessionStore } from '../lib/coachingSessionStore';
import { createChatSession } from '../lib/api';
import AppLayout from '../components/AppLayout';
import { coachPersonas } from '../lib/personas';
import { getCoachImage, hasCoachImage } from '../lib/coachImages';

const CoachingPage = () => {
  const [, navigate] = useLocation();
  const { getToken } = useAuth();
  const [selectedCoaches, setSelectedCoaches] = useState<string[]>([]);
  const [isStarting, setIsStarting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const setSelectedCoachIds = useCoachingSessionStore((state) => state.setSelectedCoachIds);
  const setSessionMeta = useCoachingSessionStore((state) => state.setSessionMeta);

  const handleCoachToggle = (coachId: string) => {
    setValidationError(null);
    if (selectedCoaches.includes(coachId)) {
      // Remove coaches if already selected
      setSelectedCoaches(selectedCoaches.filter(id => id !== coachId));
    } else {
      // Add coache if not already selected (limit to 3 coaches)
      if (selectedCoaches.length < 3) {
        setSelectedCoaches([...selectedCoaches, coachId]);
      }
    }
  };

  const handleStartSession = async () => {
    if (selectedCoaches.length === 0) {
      setValidationError('Please select at least one coach to start a session');
      return;
    }

    setIsStarting(true);
    setErrorMessage(null);
    setValidationError(null);

    try {
      const token = await getToken();
      if (!token) {
        throw new Error('We could not authenticate your session. Please sign in again.');
      }

      setSelectedCoachIds(selectedCoaches);

      try {
        const session = await createChatSession(selectedCoaches, token);

        if (session) {
          setSessionMeta({
            sessionId: session.id,
            title: session.title,
            coachIds: session.coachTypes,
          });
          navigate(`/coaching/session?session=${session.id}`);
          return;
        }
      } catch (apiError) {
        console.error('API connection error:', apiError);
        // If API connection fails, create a mock session
        const mockSession = {
          id: `local_${Date.now().toString(36)}`,
          title: `Session with ${selectedCoaches.join(' + ')}`,
          coachTypes: selectedCoaches,
        };

        setSessionMeta({
          sessionId: mockSession.id,
          title: mockSession.title,
          coachIds: mockSession.coachTypes,
        });

        // Navigate to the session page with the mock session
        navigate(`/coaching/session?coaches=${encodeURIComponent(selectedCoaches.join(','))}`);
        return;
      }

      // Fallback if session creation fails but doesn't throw an error
      navigate('/coaching/session');
    } catch (error) {
      console.error('Failed to start coaching session', error);
      setErrorMessage(error instanceof Error ? error.message : 'Unable to start session. Please try again.');
      setIsStarting(false);
    }
  };

  return (
    <AppLayout currentPage="coaching">
      <div className="min-h-screen flex flex-col">
        {/* Header */}
        <div className="px-4 py-6 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="text-center space-y-3">
              <h1 className="font-display text-3xl font-semibold text-slate-900 tracking-tight">
                Choose Your Coach
              </h1>
              <div className="text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto space-y-1">
                <p>Choose the coach that best fits your current need.</p>
                <p>Switch coaches as your priorities evolve.</p>
                <p>Combine coaches when your situation calls for multiple perspectives.</p>
              </div>
              {validationError && (
                <div className="mt-2 flex items-center justify-center gap-2 text-amber-600 font-medium">
                  <AlertCircle className="h-4 w-4" />
                  <p>{validationError}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Coach Selection - Moved to top */}
        <div className="px-4 py-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 mobile-card-padding">
              {coachPersonas.map((coach) => {
                const isSelected = selectedCoaches.includes(coach.id);

                return (
                  <button
                    key={coach.id}
                    onClick={() => handleCoachToggle(coach.id)}
                    className={`group relative flex items-center text-left gap-4 rounded-xl p-4 transition-all duration-200 ${isSelected
                        ? 'bg-blue-50 border-2 border-[#4C62E3] shadow-sm'
                        : 'bg-white/80 border border-slate-200 hover:border-[#4C62E3] hover:bg-white hover:shadow-sm'
                      }`}
                    aria-pressed={isSelected}
                  >
                    {/* Coach Avatar */}
                    <div className="h-16 w-16 rounded-full overflow-hidden shadow-sm bg-white border border-gray-100 flex-shrink-0">
                      {hasCoachImage(coach.id) ? (
                        <img
                          src={getCoachImage(coach.id)}
                          alt={`${coach.name}`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            target.nextElementSibling?.classList.remove('hidden');
                          }}
                        />
                      ) : null}
                      <div className={`w-full h-full bg-gradient-to-br ${coach.color} flex items-center justify-center ${hasCoachImage(coach.id) ? 'hidden' : ''}`}>
                        <span className="text-xl font-semibold text-white">
                          {coach.name.charAt(0)}
                        </span>
                      </div>
                    </div>

                    {/* Coach Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-display text-lg font-semibold text-slate-900 mb-0.5">
                            {coach.name}
                          </h3>
                          <p className="text-xs font-display font-medium text-[#4C62E3] uppercase tracking-wider mb-1.5">
                            {coach.title}
                          </p>
                        </div>
                      </div>

                      <p className="text-xs text-slate-600 line-clamp-2 mb-2">
                        {coach.bio}
                      </p>

                      <div className="flex flex-wrap gap-1.5">
                        {(coach.highlightTags ?? []).map((specialty) => (
                          <span
                            key={specialty}
                            className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-display font-medium text-gray-700"
                          >
                            {specialty}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Selection Indicator */}
                    <div className={`absolute top-2 right-2 flex h-6 w-6 items-center justify-center rounded-full transition-all duration-200 ${isSelected ? 'bg-[#4C62E3] text-white scale-100' : 'bg-gray-100 text-transparent scale-90 opacity-0'}`}>
                      <span className="text-xs font-semibold text-white">
                        {isSelected ? selectedCoaches.indexOf(coach.id) + 1 : ''}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Selected Coaches & Start Button */}
        <div className="sticky bottom-0 bg-white/95 backdrop-blur-xl border-t border-slate-200/60 px-4 py-4 sm:px-6 lg:px-8 mt-auto mobile-bottom-spacing">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              {/* Selected Coaches Display */}
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-slate-700">
                  Selected: {selectedCoaches.length > 0 ? '' : 'None'}
                </span>
                <div className="flex flex-wrap items-center gap-2">
                  {selectedCoaches.map((coachId) => {
                    const coach = coachPersonas.find(p => p.id === coachId);
                    if (!coach) return null;

                    return (
                      <div
                        key={coachId}
                        className="group relative flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1.5 border border-blue-200"
                      >
                        <div className="h-5 w-5 rounded-full overflow-hidden bg-white border border-gray-200">
                          {hasCoachImage(coach.id) ? (
                            <img
                              src={getCoachImage(coach.id)}
                              alt={coach.name}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                target.nextElementSibling?.classList.remove('hidden');
                              }}
                            />
                          ) : null}
                          <div className={`w-full h-full bg-gradient-to-br ${coach.color} flex items-center justify-center text-white text-xs font-semibold ${hasCoachImage(coach.id) ? 'hidden' : ''}`}>
                            {coach.name.charAt(0)}
                          </div>
                        </div>
                        <span className="text-xs font-medium text-slate-900">
                          {coach.name}
                        </span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCoachToggle(coachId);
                          }}
                          aria-label={`Remove ${coach.name}`}
                          title={`Remove ${coach.name}`}
                          className="ml-1 flex h-5 w-5 items-center justify-center rounded-full bg-slate-200 hover:bg-red-100 text-slate-500 hover:text-red-600 transition-colors"
                        >
                          <X className="h-3 w-3" strokeWidth={2} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Start Session Button */}
              <button
                onClick={handleStartSession}
                disabled={isStarting}
                className={`inline-flex items-center justify-center gap-2 rounded-xl px-6 py-3 text-base font-medium shadow-sm transition-all duration-200 ${isStarting ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : 'bg-royal-blue text-white hover:bg-royal-blue/90 hover:shadow-md'}`}
              >
                {isStarting ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" strokeWidth={1.5} />
                    <span>Starting...</span>
                  </>
                ) : (
                  <>
                    <Play className="h-5 w-5" strokeWidth={1.5} />
                    <span className='text-white'>Start Coaching Session</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="fixed bottom-20 left-1/2 transform -translate-x-1/2 w-full max-w-md px-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 shadow-lg">
              <p className="text-sm text-red-600 font-medium">{errorMessage}</p>
              <button
                onClick={() => setErrorMessage(null)}
                className="mt-2 inline-flex items-center gap-2 rounded-md bg-red-100 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-200 transition-colors"
              >
                <span>Dismiss</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
};

export default CoachingPage;
